import { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { logError, logInfo } from '../utils/logger';

export const useUserSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    let unsubscribe;

    const initializeSettings = async (user) => {
      if (!user) {
        setSettings(null);
        setLoading(false);
        return;
      }

      try {
        const settingsRef = doc(db, 'userSettings', user.uid);
        
        unsubscribe = onSnapshot(settingsRef, async (snapshot) => {
          if (!snapshot.exists()) {
            // Create default settings if they don't exist
            await setDoc(settingsRef, {
              preferences: {
                mapSettings: {
                  defaultMapType: 'roadmap',
                  poiSize: 'medium',
                  clusterPOIs: true,
                  clusterThreshold: '50',
                  clusterSize: 'medium',
                  showPOILabels: true,
                  showPOIInfo: true,
                  showPOIMeasurements: true,
                  showRouteNames: true,
                  showDistanceMarkers: true,
                  showRouteDirections: false,
                  showTraffic: false,
                  showStreetView: false,
                  showWeather: false,
                  showTeamMembers: true,
                  showCompass: false,
                  showScaleBar: false,
                  showToolbar: true
                },
                language: 'en',
                notifications: {
                  enabled: true,
                  email: true,
                  weatherAlerts: true,
                  roadConditions: true
                },
                autoLogin: false,
                darkMode: false,
                units: 'metric'
              },
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            });
            setSettings({
              preferences: {
                mapSettings: {
                  defaultMapType: 'roadmap',
                  poiSize: 'medium',
                  clusterPOIs: true,
                  showPOILabels: true,
                  showPOIInfo: true,
                  showPOIMeasurements: true,
                  showRouteNames: true,
                  showDistanceMarkers: true,
                  showRouteDirections: false,
                  showTraffic: false,
                  showStreetView: false,
                  showWeather: false,
                  showTeamMembers: true,
                  showCompass: false,
                  showScaleBar: false,
                  showToolbar: true
                }
              }
            });
          } else {
            setSettings(snapshot.data());
          }
          setLoading(false);
        }, (err) => {
          if (err.code === 'permission-denied') {
            setSettings(null);
            setLoading(false);
          } else {
            logError('Error fetching settings:', err);
            setError('Failed to load settings');
            setLoading(false);
          }
        });
      } catch (error) {
        logError('Failed to initialize settings:', error);
        setError('Failed to initialize settings');
        setLoading(false);
      }
    };

    const unsubscribeAuth = auth.onAuthStateChanged(initializeSettings);

    return () => {
      if (unsubscribe) unsubscribe();
      unsubscribeAuth();
    };
  }, []);

  const updateSettings = async (updates) => {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('No authenticated user');
    }

    try {
      const settingsRef = doc(db, 'userSettings', auth.currentUser.uid);
      await updateDoc(settingsRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      });
      logInfo('Settings updated successfully');
    } catch (err) {
      logError('Error updating settings:', err);
      throw new Error('Failed to update settings');
    }
  };

  return {
    settings,
    loading,
    error,
    updateSettings
  };
};